import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { MemberIdentity } from '../memberDisplay';

/**
 * Тумблер «Случайный порядок на старте» (для секции участников).
 * value = true  → случайный посев (дефолт),
 * value = false → ручной порядок (показываем перетаскиваемый список).
 *
 * @param {boolean}  value
 * @param {Function} onChange(nextBool)
 * @param {boolean}  [disabled]
 */
export const RandomOrderToggle = ({ value, onChange, disabled }) => (
  <button
    type="button"
    onClick={() => !disabled && onChange(!value)}
    disabled={disabled}
    title={value ? 'Игроки распределяются случайно на старте' : 'Ручной порядок игроков'}
    style={{
      display: 'flex', alignItems: 'center', gap: 6,
      background: 'transparent', border: 'none', padding: 0,
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.5 : 1,
    }}
  >
    <span style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'none', letterSpacing: 0 }}>
      Случайный порядок на старте
    </span>
    <span style={{
      position: 'relative', width: 36, height: 20, borderRadius: 10,
      background: value ? '#d97706' : '#d1d5db',
      flexShrink: 0, transition: 'background 0.2s',
    }}>
      <span style={{
        position: 'absolute', top: 2, left: value ? 18 : 2,
        width: 16, height: 16, borderRadius: '50%', background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transition: 'left 0.2s',
      }} />
    </span>
  </button>
);

// Иконка «грип» (точки для перетаскивания)
const GripIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="#9ca3af" aria-hidden="true">
    <circle cx="9" cy="6" r="1.6" /><circle cx="15" cy="6" r="1.6" />
    <circle cx="9" cy="12" r="1.6" /><circle cx="15" cy="12" r="1.6" />
    <circle cx="9" cy="18" r="1.6" /><circle cx="15" cy="18" r="1.6" />
  </svg>
);

// Маленькая кнопка-стрелка ↑/↓
const ArrowBtn = ({ dir, onClick, disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={dir === 'up' ? 'Вверх' : 'Вниз'}
    style={{
      width: 26, height: 22, borderRadius: 6,
      border: '1px solid #e5e7eb', background: disabled ? '#f9fafb' : '#fff',
      color: disabled ? '#d1d5db' : '#6b7280',
      cursor: disabled ? 'default' : 'pointer',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 0, lineHeight: 0,
    }}
  >
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      {dir === 'up' ? <polyline points="18 15 12 9 6 15" /> : <polyline points="6 9 12 15 18 9" />}
    </svg>
  </button>
);

// Длительность плавного «доезда» плашек (стрелки + возврат после drag).
const SETTLE_MS = 320;
const SETTLE_EASE = 'cubic-bezier(0.22, 0.61, 0.36, 1)';
// Как быстро расступаются соседи при перетаскивании.
const SHIFT_MS = 180;
const ROW_GAP = 6; // должен совпадать с gap контейнера строк

/**
 * Перетаскиваемый список игроков (ручной посев).
 *
 * Перетаскивание за «грип» справа (рядом со стрелками) через Pointer Events —
 * работает и мышью, и тачем. Схваченная плашка ВИЗУАЛЬНО поднимается и едет за
 * курсором/пальцем, а соседние строки плавно расступаются, освобождая место;
 * на отпускании порядок фиксируется и плашка мягко «доезжает» на новое место.
 * Плюс кнопки ↑/↓ как надёжный фолбэк (с той же плавной анимацией переезда).
 *
 * Контролируемый по items; локально отражает порядок, на изменение зовёт
 * onReorder(orderedItems).
 *
 * @param {Array}    items     — игроки в текущем порядке (объекты для MemberIdentity)
 * @param {Function} getKey    — уникальный ключ элемента (по умолчанию organizationMemberId ?? id)
 * @param {Function} onReorder(orderedItems)
 * @param {Function} [renderTrailing](item) — доп. контрол справа от строки (например тумблер)
 * @param {Function} [renderAnnotation](item) — доп. подпись под именем (например «удалён из события»)
 * @param {boolean}  [disabled]
 */
export const PlayerReorderList = ({
  items,
  getKey = (it) => it.organizationMemberId ?? it.id,
  onReorder,
  renderTrailing,
  renderAnnotation,
  disabled,
}) => {
  const [order, setOrder] = useState(items);
  const orderRef = useRef(items);
  const rowRefs = useRef({});           // key → DOM-узел строки
  const prevRects = useRef({});         // key → прошлый bounding rect (для FLIP-анимации стрелок)
  const dragRef = useRef(null);         // активный drag: { key, fromIndex, toIndex, startY, pitch, keys } | null
  const [dragKey, setDragKey] = useState(null); // только для стилей (курсор/подсветка)

  // Синхронизация при изменении состава/порядка извне (по сигнатуре ключей).
  // Во время перетаскивания не трогаем — иначе оборвётся drag.
  const sig = items.map(getKey).join(',');
  useEffect(() => {
    if (dragRef.current) return;
    // Наш собственный reorder «эхом» вернулся от родителя тем же порядком —
    // не пере-сетим (лишний рендер прервал бы идущую FLIP-анимацию).
    if (orderRef.current.map(getKey).join(',') === sig) return;
    setOrder(items);
    orderRef.current = items;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);

  // ── FLIP-анимация переезда (для стрелок и «доезда» после drag) ─────────────
  // После рендера сравниваем прошлые позиции строк с новыми, мгновенно «возвращаем»
  // каждую на старое место через transform (без перехода), затем снимаем его с
  // переходом — строки плавно доезжают. Во время активного drag не вмешиваемся
  // (там позиции задаются императивно в обработчиках указателя).
  useLayoutEffect(() => {
    const newRects = {};
    Object.keys(rowRefs.current).forEach((k) => {
      const node = rowRefs.current[k];
      if (node) newRects[k] = node.getBoundingClientRect();
    });
    if (!dragRef.current) {
      Object.keys(newRects).forEach((k) => {
        const prev = prevRects.current[k];
        const node = rowRefs.current[k];
        if (!prev || !node) return;
        const dy = prev.top - newRects[k].top;
        if (!dy) return;
        node.style.transition = 'none';
        node.style.transform = `translateY(${dy}px)`;
        // Форсируем reflow, чтобы стартовое смещение применилось до перехода.
        void node.getBoundingClientRect();
        node.style.transition = `transform ${SETTLE_MS}ms ${SETTLE_EASE}`;
        node.style.transform = '';
      });
    }
    prevRects.current = newRects;
  });

  // ── Перестановка стрелками ─────────────────────────────────────────────────
  const idxOf = (k) => orderRef.current.findIndex((it) => getKey(it) === k);
  const commit = (next) => { orderRef.current = next; onReorder?.(next); };

  const moveBy = (k, delta) => {
    const cur = orderRef.current;
    const i = idxOf(k);
    const j = i + delta;
    if (i < 0 || j < 0 || j >= cur.length) return;
    const next = cur.slice();
    const [m] = next.splice(i, 1);
    next.splice(j, 0, m);
    setOrder(next);        // FLIP анимирует переезд
    commit(next);
  };

  // ── Перетаскивание (Pointer Events, императивно — без ре-рендера на каждый шаг) ─
  const applyLift = (node) => {
    if (!node) return;
    node.style.zIndex = '20';
    node.style.boxShadow = '0 10px 24px rgba(0,0,0,0.18)';
    node.style.background = '#fff7ed';
    node.style.transition = 'none';
  };
  const resetNode = (node) => {
    if (!node) return;
    node.style.transform = '';
    node.style.transition = '';
    node.style.zIndex = '';
    node.style.boxShadow = '';
    node.style.background = '';
  };

  const measurePitch = (keys) => {
    const a = rowRefs.current[keys[0]];
    const b = keys.length > 1 ? rowRefs.current[keys[1]] : null;
    if (a && b) return b.getBoundingClientRect().top - a.getBoundingClientRect().top;
    if (a) return a.getBoundingClientRect().height + ROW_GAP;
    return 0;
  };

  const onDragStart = (e, key) => {
    if (disabled) return;
    e.preventDefault();
    const cur = orderRef.current;
    const fromIndex = cur.findIndex((it) => getKey(it) === key);
    if (fromIndex < 0) return;
    const keys = cur.map(getKey);
    dragRef.current = { key, fromIndex, toIndex: fromIndex, startY: e.clientY, pitch: measurePitch(keys), keys };
    setDragKey(key);
    applyLift(rowRefs.current[key]);
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (_) {}
  };

  const onDragMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    e.preventDefault();
    const dy = e.clientY - d.startY;
    // Схваченная плашка следует за курсором 1:1.
    const dragNode = rowRefs.current[d.key];
    if (dragNode) dragNode.style.transform = `translateY(${dy}px)`;

    const steps = d.pitch ? Math.round(dy / d.pitch) : 0;
    const toIndex = Math.max(0, Math.min(d.keys.length - 1, d.fromIndex + steps));
    d.toIndex = toIndex;

    // Соседи между исходной и целевой позицией расступаются на высоту строки.
    for (let i = 0; i < d.keys.length; i++) {
      const kk = d.keys[i];
      if (kk === d.key) continue;
      const node = rowRefs.current[kk];
      if (!node) continue;
      let shift = 0;
      if (d.fromIndex < toIndex && i > d.fromIndex && i <= toIndex) shift = -d.pitch;
      else if (d.fromIndex > toIndex && i >= toIndex && i < d.fromIndex) shift = d.pitch;
      node.style.transition = `transform ${SHIFT_MS}ms ${SETTLE_EASE}`;
      node.style.transform = shift ? `translateY(${shift}px)` : 'translateY(0)';
    }
  };

  const onDragEnd = (e) => {
    const d = dragRef.current;
    if (!d) return;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {}
    const { fromIndex, toIndex, keys } = d;

    // Снимок текущих (визуальных, со смещениями) позиций — чтобы FLIP плавно
    // довёл строки из того места, где они реально сейчас, до нового layout.
    const visualRects = {};
    keys.forEach((kk) => {
      const n = rowRefs.current[kk];
      if (n) visualRects[kk] = n.getBoundingClientRect();
    });
    keys.forEach((kk) => resetNode(rowRefs.current[kk]));

    dragRef.current = null;
    setDragKey(null);
    prevRects.current = visualRects;

    if (fromIndex !== toIndex) {
      const cur = orderRef.current;
      const next = cur.slice();
      const [m] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, m);
      setOrder(next);
      commit(next);
    } else {
      // Порядок не изменился — просто мягко возвращаем поднятую плашку на место.
      setOrder(orderRef.current.slice());
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: ROW_GAP }}>
      {order.map((item, idx) => {
        const k = getKey(item);
        const isDragging = dragKey === k;
        return (
          <div
            key={k}
            ref={(el) => { rowRefs.current[k] = el; }}
            style={{
              position: 'relative',
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 10px', borderRadius: 10,
              border: '1.5px solid #f0f0f5',
              background: '#fff',
              opacity: disabled ? 0.6 : 1,
              willChange: 'transform',
            }}
          >
            {/* Порядковый номер */}
            <span style={{
              fontSize: 12, fontWeight: 700, color: '#9ca3af',
              width: 18, textAlign: 'center', flexShrink: 0,
            }}>
              {idx + 1}
            </span>

            {/* Игрок */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <MemberIdentity member={item} size={30} />
              {renderAnnotation && renderAnnotation(item)}
            </div>

            {/* Доп. контрол (например тумблер активности) */}
            {renderTrailing && (
              <div style={{ flexShrink: 0 }}>
                {renderTrailing(item)}
              </div>
            )}

            {/* Грип — область перетаскивания (справа, рядом со стрелками) */}
            <span
              onPointerDown={(e) => onDragStart(e, k)}
              onPointerMove={onDragMove}
              onPointerUp={onDragEnd}
              onPointerCancel={onDragEnd}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 34, flexShrink: 0, borderRadius: 8,
                cursor: disabled ? 'default' : (isDragging ? 'grabbing' : 'grab'),
                background: isDragging ? '#ffedd5' : 'transparent',
                touchAction: 'none',
              }}
            >
              <GripIcon />
            </span>

            {/* Стрелки */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, flexShrink: 0 }}>
              <ArrowBtn dir="up"   onClick={() => moveBy(k, -1)} disabled={disabled || idx === 0} />
              <ArrowBtn dir="down" onClick={() => moveBy(k, +1)} disabled={disabled || idx === order.length - 1} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default PlayerReorderList;
